import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import { Checkbox, Input } from "./FormAtoms.jsx";
import { usePortaledMenuPosition } from "./usePortaledMenuPosition.js";

function normalizeOptions(options = []) {
  return (options || []).map((o) => ({
    value: o?.value,
    label: String(o?.label ?? o?.value ?? ""),
  }));
}

function useClickOutside(anchorRef, listRef, onOutside, active) {
  useEffect(() => {
    if (!active) return undefined;
    const onDown = (e) => {
      const inAnchor = anchorRef.current?.contains(e.target);
      const inList = listRef.current?.contains(e.target);
      if (!inAnchor && !inList) onOutside?.();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [anchorRef, listRef, onOutside, active]);
}

const portaledListClassName =
  "overflow-y-auto overscroll-contain rounded-xl border border-white/12 bg-[#0b1324] shadow-[0_16px_48px_rgba(0,0,0,0.45)] custom-scrollbar";

/**
 * Ein Feld: tippen = suchen, Liste direkt darunter (kein separates Suchfeld).
 */
export function MobileSearchPickSingle({
  inputId,
  options = [],
  value = null,
  onChange,
  placeholder = "Auswählen…",
  disabled = false,
  isLoading = false,
  emptyMessage = "Keine Treffer",
  isClearable = false,
  onBlur,
  "aria-invalid": ariaInvalid,
  onQueryChange,
  debounceMs = 280,
}) {
  const wrapRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const menuStyle = usePortaledMenuPosition(wrapRef, open, { maxHeight: 360 });

  const opts = useMemo(() => normalizeOptions(options), [options]);
  const selected = useMemo(() => {
    if (value == null || value === "") return null;
    if (typeof value === "object" && "value" in value) return value;
    return opts.find((o) => String(o.value) === String(value)) || null;
  }, [value, opts]);

  const localFiltered = useMemo(() => {
    if (onQueryChange) return opts;
    const q = query.trim().toLowerCase();
    if (!q) return opts;
    return opts.filter((o) => o.label.toLowerCase().includes(q));
  }, [opts, query, onQueryChange]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
    onBlur?.();
  }, [onBlur]);

  useClickOutside(wrapRef, listRef, close, open);

  useEffect(() => {
    if (!open || !onQueryChange) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onQueryChange(query);
    }, debounceMs);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [open, query, onQueryChange, debounceMs]);

  useEffect(() => {
    if (open) return;
    setQuery("");
  }, [open]);

  const openPicker = useCallback(() => {
    if (disabled || isLoading) return;
    setOpen(true);
    setQuery("");
    onQueryChange?.("");
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [disabled, isLoading, onQueryChange]);

  const inputDisplay = open ? query : selected?.label || "";

  const pick = useCallback(
    (opt) => {
      onChange?.(opt);
      setOpen(false);
      setQuery("");
      onBlur?.();
    },
    [onChange, onBlur],
  );

  return (
    <div ref={wrapRef} className="relative min-w-0">
      <div className="relative">
        <Input
          ref={inputRef}
          id={inputId}
          type="search"
          inputMode="search"
          enterKeyHint="search"
          autoComplete="off"
          value={inputDisplay}
          readOnly={!open && Boolean(selected)}
          onFocus={openPicker}
          onClick={!open ? openPicker : undefined}
          onChange={(e) => {
            if (!open) setOpen(true);
            setQuery(e.target.value);
            if (!onQueryChange) return;
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => onQueryChange(e.target.value), debounceMs);
          }}
          placeholder={placeholder}
          disabled={disabled || isLoading}
          aria-invalid={ariaInvalid}
          aria-expanded={open ? "true" : "false"}
          aria-haspopup="listbox"
          className={!open && selected ? "pr-9" : "pr-3"}
        />
        <ChevronDown
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-200/50"
          aria-hidden
        />
      </div>

      {open && menuStyle && typeof document !== "undefined"
        ? createPortal(
            <ul
              ref={listRef}
              role="listbox"
              className={portaledListClassName}
              style={menuStyle}
            >
              {isLoading ? (
                <li className="px-3 py-3 text-sm text-blue-200/60">Lädt…</li>
              ) : localFiltered.length === 0 ? (
                <li className="px-3 py-3 text-sm text-blue-200/60">{emptyMessage}</li>
              ) : (
                localFiltered.map((o) => {
                  const active = String(o.value) === String(selected?.value);
                  return (
                    <li key={String(o.value)} role="option" aria-selected={active}>
                      <button
                        type="button"
                        className={`w-full text-left px-3 py-3 min-h-[48px] border-b border-white/5 last:border-b-0 text-sm leading-snug ${
                          active ? "bg-blue-600/25 text-blue-50" : "text-blue-100/90 active:bg-white/5"
                        }`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => pick(o)}
                      >
                        {o.label}
                      </button>
                    </li>
                  );
                })
              )}
            </ul>,
            document.body,
          )
        : null}

      {isClearable && selected && !open ? (
        <button
          type="button"
          className="mt-1 text-xs text-blue-300/80 min-h-[36px]"
          onClick={() => onChange?.(null)}
          disabled={disabled}
        >
          Auswahl löschen
        </button>
      ) : null}
    </div>
  );
}

function formatSummary(selected = [], placeholder = "Auswählen…") {
  if (!selected.length) return placeholder;
  if (selected.length === 1) return selected[0].label;
  const names = selected.map((o) => o.label).slice(0, 2).join(", ");
  const rest = selected.length > 2 ? ` +${selected.length - 2}` : "";
  return `${names}${rest}`;
}

/**
 * Wie Kunde: ein Suchfeld, Liste darunter, Häkchen ohne Schliessen — «Fertig» nur zum Zuklappen.
 */
export function MobileSearchPickMulti({
  inputId,
  options = [],
  value = [],
  onChange,
  placeholder = "Mitarbeitende auswählen…",
  disabled = false,
  isLoading = false,
  emptyMessage = "Keine Treffer",
  isClearable = false,
  pickPlaceholder = "Suchen und auswählen…",
}) {
  const wrapRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const menuStyle = usePortaledMenuPosition(wrapRef, open, { maxHeight: 360 });

  const opts = useMemo(() => normalizeOptions(options), [options]);
  const selected = useMemo(() => {
    const arr = Array.isArray(value) ? value : [];
    return arr
      .map((v) => {
        if (v && typeof v === "object" && "value" in v) return v;
        return opts.find((o) => String(o.value) === String(v)) || null;
      })
      .filter(Boolean);
  }, [value, opts]);

  const selectedIds = useMemo(
    () => new Set(selected.map((o) => String(o.value))),
    [selected],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return opts;
    return opts.filter((o) => o.label.toLowerCase().includes(q));
  }, [opts, query]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
  }, []);

  useClickOutside(wrapRef, listRef, close, open);

  const emitChange = useCallback(
    (nextIds) => {
      const next = opts.filter((o) => nextIds.has(String(o.value)));
      onChange?.(next);
    },
    [onChange, opts],
  );

  const toggleId = useCallback(
    (id) => {
      const key = String(id);
      const next = new Set(selectedIds);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      emitChange(next);
    },
    [selectedIds, emitChange],
  );

  const openPicker = useCallback(() => {
    if (disabled || isLoading) return;
    setOpen(true);
    setQuery("");
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [disabled, isLoading]);

  const summary = formatSummary(selected, isLoading ? "Lädt…" : "");
  const inputDisplay = open ? query : summary;
  const inputPlaceholder = open ? pickPlaceholder : placeholder;

  return (
    <div ref={wrapRef} className="relative min-w-0">
      <div className="relative">
        <Input
          ref={inputRef}
          id={inputId}
          type="search"
          inputMode="search"
          enterKeyHint={open ? "done" : "search"}
          autoComplete="off"
          value={inputDisplay}
          readOnly={!open && selected.length > 0}
          onFocus={openPicker}
          onClick={!open ? openPicker : undefined}
          onChange={(e) => {
            if (!open) setOpen(true);
            setQuery(e.target.value);
          }}
          placeholder={inputPlaceholder}
          disabled={disabled || isLoading}
          aria-expanded={open ? "true" : "false"}
          aria-haspopup="listbox"
          aria-multiselectable="true"
          className={!open && selected.length ? "pr-9" : "pr-3"}
        />
        <ChevronDown
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-200/50"
          aria-hidden
        />
      </div>

      {open ? (
        <>
          {menuStyle && typeof document !== "undefined"
            ? createPortal(
                <ul
                  ref={listRef}
                  role="listbox"
                  aria-multiselectable="true"
                  className={portaledListClassName}
                  style={menuStyle}
                >
                  {isLoading ? (
                    <li className="px-3 py-3 text-sm text-blue-200/60">Lädt…</li>
                  ) : filtered.length === 0 ? (
                    <li className="px-3 py-3 text-sm text-blue-200/60">{emptyMessage}</li>
                  ) : (
                    filtered.map((o) => {
                      const checked = selectedIds.has(String(o.value));
                      return (
                        <li key={String(o.value)} role="option" aria-selected={checked}>
                          <button
                            type="button"
                            className={`w-full flex items-center gap-3 min-h-[48px] px-3 py-2 text-left border-b border-white/5 last:border-b-0 ${
                              checked ? "bg-blue-600/15" : "active:bg-white/5"
                            }`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => toggleId(o.value)}
                          >
                            <Checkbox
                              checked={checked}
                              disabled={disabled}
                              readOnly
                              tabIndex={-1}
                              className="pointer-events-none"
                            />
                            <span className="text-sm text-blue-100/90 leading-snug">{o.label}</span>
                          </button>
                        </li>
                      );
                    })
                  )}
                </ul>,
                document.body,
              )
            : null}
          <div className="flex items-center justify-between gap-2 mt-2 min-h-[44px]">
            {isClearable && selected.length > 0 ? (
              <button
                type="button"
                className="text-xs text-blue-300/80 px-1"
                onClick={() => onChange?.([])}
                disabled={disabled}
              >
                Alle abwählen
              </button>
            ) : (
              <span />
            )}
            <button
              type="button"
              className="text-sm font-medium text-blue-300 px-3 py-2 min-h-[44px]"
              onClick={close}
            >
              Fertig
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}
